import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Modal from '../ui/Modal';
import customerService from '../../services/customerService';

/**
 * Replacing the card a plan charges.
 *
 * A SETUP INTENT, NOT A PAYMENT. Changing a card costs nothing, and charging a
 * token amount to verify one would put a surprise on someone's statement. The
 * new method is attached to the customer and made the subscription's default
 * once they confirm it.
 *
 * DIRECT CHARGES: the customer and the card live on the HOST'S connected
 * account, so stripe.js must be loaded with `stripeAccount` or the intent is
 * not found. Same requirement as every other payment surface here.
 *
 * Someone whose card is expiring previously had no way to see which card was on
 * file, let alone change it — the only options were to let the renewal fail or
 * cancel the plan.
 */

interface UpdateCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId: string;
  /** The card is now changed — the caller re-reads it. */
  onUpdated: () => void;
}

interface SetupInit {
  clientSecret: string;
  publishableKey: string;
  connectedAccountId: string;
}

const UpdateCardModal: React.FC<UpdateCardModalProps> = ({
  isOpen,
  onClose,
  subscriptionId,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const [init, setInit] = useState<SetupInit | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setInit(null);
      setError(null);
      return;
    }
    let cancelled = false;
    customerService
      .startCardUpdate(subscriptionId)
      .then((result) => {
        if (cancelled) return;
        if (!result.clientSecret || !result.publishableKey) {
          setError(
            t('booking:points.cardUpdateUnavailable', 'Card updates are not available right now.')
          );
          return;
        }
        setInit({
          clientSecret: result.clientSecret,
          publishableKey: result.publishableKey,
          connectedAccountId: result.connectedAccountId,
        });
      })
      .catch(() =>
        !cancelled &&
        setError(t('booking:points.cardUpdateFailed', 'Could not start the card update.'))
      );
    return () => {
      cancelled = true;
    };
  }, [isOpen, subscriptionId, t]);

  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () =>
      init ? loadStripe(init.publishableKey, { stripeAccount: init.connectedAccountId }) : null,
    [init]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('booking:points.updateCard', 'Update card')}
      size="small"
    >
      {error && <p className="text-sm text-red-600">{error}</p>}

      {init && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: init.clientSecret, appearance: { theme: 'stripe' as const } }}
        >
          <CardForm subscriptionId={subscriptionId} onClose={onClose} onUpdated={onUpdated} />
        </Elements>
      )}

      {!init && !error && (
        <p className="text-sm text-gray-500">{t('common:status.loading', 'Loading...')}</p>
      )}
    </Modal>
  );
};

const CardForm: React.FC<{
  subscriptionId: string;
  onClose: () => void;
  onUpdated: () => void;
}> = ({ subscriptionId, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setError(null);
    // Wrapped so `submitting` is always released — a rejection here otherwise
    // leaves the button stuck with no way back.
    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message || t('booking:points.cardUpdateFailed', 'Could not save that card.'));
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;

      if (!paymentMethodId) {
        setError(t('booking:points.cardUpdateFailed', 'Could not save that card.'));
        return;
      }

      // Stripe has the card; this is what points the PLAN at it. Without this
      // the card is merely attached and the next renewal charges the old one.
      await customerService.finishCardUpdate(subscriptionId, paymentMethodId);
      onUpdated();
      onClose();
    } catch (err: any) {
      setError(err?.message || t('booking:points.cardUpdateFailed', 'Could not save that card.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-3">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
          {t('common:actions.cancel', 'Cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={!stripe || submitting}>
          {submitting
            ? t('common:status.processing', 'Processing...')
            : t('booking:points.saveCard', 'Save card')}
        </button>
      </div>
    </form>
  );
};

export default UpdateCardModal;
